import { DEFAULT_OWNER_WORKSPACE_FILTERS, type OwnerWorkspaceFilters } from 'config/api/assetRopa/assetRopaFilter.types';
import type { OwnerWorkspaceFilterOptions } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import processing_activities_messages from 'messages/processing_activities_messages';

import {
  buildDpiaCombinedStatusDrawerSection,
  buildDpiaIndicationDrawerSection,
  readDpiaFiltersFromTableSections,
  writeDpiaFiltersToTableSections,
} from '../_shared/apaDpiaDrawerFilters';

type CompletenessMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.completeness_status;
type GdprRelevanceMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance;

const COMPLETENESS_OPTIONS: CompletenessMessageKey[] = ['complete', 'incomplete', 'no_processing_activities'];
const GDPR_RELEVANCE_OPTIONS: GdprRelevanceMessageKey[] = ['currently_indicated', 'no_longer_indicated', 'all'];
const BOOLEAN_FILTER_OPTIONS = ['true', 'false'] as const;

export type OwnerWorkspaceTableFilterChip = {
  id: string;
  name: string | string[];
};

export type OwnerWorkspaceTableFilterState = {
  asset_type?: OwnerWorkspaceTableFilterChip[];
  completeness?: OwnerWorkspaceTableFilterChip[];
  gdpr_relevance?: OwnerWorkspaceTableFilterChip[];
  dpia_indication?: OwnerWorkspaceTableFilterChip[];
  dpia_status?: OwnerWorkspaceTableFilterChip[];
  sensitive_personal_data?: OwnerWorkspaceTableFilterChip[];
  third_country_processing?: OwnerWorkspaceTableFilterChip[];
};

export type OwnerWorkspaceTableAvailableFilters = {
  asset_type?: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
  completeness: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
  gdpr_relevance: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
  dpia_indication: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
  dpia_status: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
    isDisabled?: boolean;
    disabledTooltip?: string[];
  };
  sensitive_personal_data: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
  third_country_processing: {
    sectionName: string[];
    filters: OwnerWorkspaceTableFilterChip[];
    isSingleSelect: boolean;
  };
};

const completenessMessage = (value: CompletenessMessageKey): string | string[] =>
  processing_activities_messages.asset_ropa.owner_workspace.completeness_status[value];

const gdprRelevanceMessage = (value: GdprRelevanceMessageKey): string | string[] =>
  processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance[value];

const booleanFilterMessage = (value: typeof BOOLEAN_FILTER_OPTIONS[number]): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'];

export const buildOwnerWorkspaceAvailableFilters = (filterOptions: OwnerWorkspaceFilterOptions): OwnerWorkspaceTableAvailableFilters => ({
  ...(filterOptions.assetTypes.length > 0
    ? {
        asset_type: {
          sectionName: processing_activities_messages.asset_ropa.owner_workspace.filters.asset_type,
          filters: filterOptions.assetTypes.map(assetType => ({
            id: String(assetType.id),
            name: assetType.name,
          })),
          isSingleSelect: true,
        },
      }
    : {}),
  completeness: {
    sectionName: processing_activities_messages.asset_ropa.owner_workspace.filters.completeness,
    filters: COMPLETENESS_OPTIONS.map(value => ({
      id: value,
      name: completenessMessage(value),
    })),
    isSingleSelect: true,
  },
  gdpr_relevance: {
    sectionName: processing_activities_messages.asset_ropa.owner_workspace.filters.gdpr_relevance,
    filters: GDPR_RELEVANCE_OPTIONS.map(value => ({
      id: value,
      name: gdprRelevanceMessage(value),
    })),
    isSingleSelect: true,
  },
  dpia_indication: buildDpiaIndicationDrawerSection(),
  dpia_status: buildDpiaCombinedStatusDrawerSection(),
  sensitive_personal_data: {
    sectionName: processing_activities_messages.asset_ropa.filters.sensitive_personal_data,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
  third_country_processing: {
    sectionName: processing_activities_messages.asset_ropa.filters.third_country_processing,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
});

export const ownerWorkspaceFilterValuesToTableFilters = (
  filters: OwnerWorkspaceFilters,
  filterOptions: OwnerWorkspaceFilterOptions = { assetTypes: [] },
): OwnerWorkspaceTableFilterState => {
  const tableFilters: OwnerWorkspaceTableFilterState = {
    ...writeDpiaFiltersToTableSections(filters),
  };

  if (filters.assetTypeId) {
    const assetTypeName = filterOptions.assetTypes.find(assetType => assetType.id === filters.assetTypeId)?.name;
    tableFilters.asset_type = [{ id: String(filters.assetTypeId), name: assetTypeName ?? String(filters.assetTypeId) }];
  }

  if (filters.completeness) {
    tableFilters.completeness = [{ id: filters.completeness, name: completenessMessage(filters.completeness) }];
  }

  if (filters.gdprRelevance !== DEFAULT_OWNER_WORKSPACE_FILTERS.gdprRelevance) {
    tableFilters.gdpr_relevance = [{ id: filters.gdprRelevance, name: gdprRelevanceMessage(filters.gdprRelevance) }];
  }

  if (filters.sensitivePersonalData) {
    tableFilters.sensitive_personal_data = [
      { id: filters.sensitivePersonalData, name: booleanFilterMessage(filters.sensitivePersonalData) },
    ];
  }

  if (filters.thirdCountryProcessing) {
    tableFilters.third_country_processing = [
      { id: filters.thirdCountryProcessing, name: booleanFilterMessage(filters.thirdCountryProcessing) },
    ];
  }

  return tableFilters;
};

export const mergeTableFiltersIntoOwnerWorkspaceFilters = (
  filters: OwnerWorkspaceFilters,
  tableFilters: OwnerWorkspaceTableFilterState,
): OwnerWorkspaceFilters => {
  const hasNoDrawerFilters = Object.keys(tableFilters).length === 0;

  if (hasNoDrawerFilters) {
    return {
      ...DEFAULT_OWNER_WORKSPACE_FILTERS,
      search: filters.search,
    };
  }

  const assetTypeIdRaw = tableFilters.asset_type?.[0]?.id;
  const parsedAssetTypeId = assetTypeIdRaw ? Number(assetTypeIdRaw) : NaN;
  const dpiaFilters = readDpiaFiltersFromTableSections(tableFilters);

  return {
    ...filters,
    assetTypeId: Number.isFinite(parsedAssetTypeId) ? parsedAssetTypeId : '',
    completeness: (tableFilters.completeness?.[0]?.id as OwnerWorkspaceFilters['completeness']) ?? '',
    gdprRelevance:
      (tableFilters.gdpr_relevance?.[0]?.id as OwnerWorkspaceFilters['gdprRelevance']) ?? DEFAULT_OWNER_WORKSPACE_FILTERS.gdprRelevance,
    ...dpiaFilters,
    sensitivePersonalData: (tableFilters.sensitive_personal_data?.[0]?.id as OwnerWorkspaceFilters['sensitivePersonalData']) ?? '',
    thirdCountryProcessing: (tableFilters.third_country_processing?.[0]?.id as OwnerWorkspaceFilters['thirdCountryProcessing']) ?? '',
  };
};
